import type { DragMoveEvent, DragStartEvent } from '@dnd-kit/react';
import { useDragDropMonitor } from '@dnd-kit/react';
import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TileData } from '../board/board-types';
import { landingIndex, moveTile, type SlotSpan } from './rack-reorder';

export interface UseRackReorderPreviewInput {
  /** The rack element, whose children are its slots in rack order. */
  rackRef: RefObject<HTMLElement | null>;
  /** The rack's tiles, already trimmed to the slots it renders. */
  tiles: TileData[];
}

export interface RackReorderPreview {
  /** The order to render right now: mid-drag, the order the tiles would be left in by a drop. */
  order: TileData[];
  /**
   * Where a release with the pointer at `x`, `y` puts the tile in hand — `null` when the pointer is
   * off the rack, or this rack has no tile in the air at all. The preview and the drop both ask this,
   * so the tile can never land anywhere other than the gap the player was shown.
   */
  landingIndexAt: (x: number, y: number) => number | null;
}

interface Bounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

const within = (bounds: Bounds, x: number, y: number): boolean =>
  x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom;

/**
 * Page coordinates, which is what everything frozen at pickup is kept in: dnd-kit auto-scrolls the
 * page as a drag nears a viewport edge, and a rack measured before that scroll would disagree with a
 * pointer read after it by however far the page moved. Pointer positions arrive from dnd-kit in
 * viewport coordinates and are put through here too.
 */
const toPage = (x: number, y: number) => ({ x: x + window.scrollX, y: y + window.scrollY });

/** Each occupied slot's span. The rack renders exactly one element per slot, in order. */
const measureSlots = (rack: HTMLElement | null, count: number): SlotSpan[] =>
  rack
    ? Array.from(rack.children)
        .slice(0, count)
        .map((slot) => {
          const { left, right } = slot.getBoundingClientRect();
          return { left: toPage(left, 0).x, right: toPage(right, 0).x };
        })
    : [];

/** The rack's own tray, in the same page coordinates. */
const measureRack = (rack: HTMLElement | null): Bounds | null => {
  if (!rack) return null;
  const { left, top, right, bottom } = rack.getBoundingClientRect();
  const topLeft = toPage(left, top);
  const bottomRight = toPage(right, bottom);
  return { left: topLeft.x, top: topLeft.y, right: bottomRight.x, bottom: bottomRight.y };
};

/**
 * Opens a gap for the tile in hand: while a rack tile is dragged over its own rack, the other tiles
 * shift to the order a drop would leave them in, rather than waiting for the drop to rearrange
 * themselves.
 *
 * The preview is driven by the pointer against slot positions frozen at pickup, with a deadband
 * either side of each swap so a pointer resting on the cusp does not chatter (see `landingIndex`),
 * and gated on the pointer being inside the rack's own box, frozen at pickup too. Dragging on out of
 * the rack, towards the board, closes the gap again: the tile would then return to where it was
 * picked up.
 *
 * Both halves of that geometry are read once, deliberately. Gating instead on dnd-kit's drop target —
 * which is what a drop is routed by — fed the preview back into itself: shuffling the tiles moves the
 * slot droppables, dnd-kit re-measures them, and for as long as that took no slot was the target, so
 * the gap snapped back to the tile's original place and then out again with every re-measure. Frozen
 * geometry cannot move under the preview that reads it, and the rack's drag end asks this same
 * `landingIndexAt`, so routing a drop and drawing the gap can never disagree.
 */
export const useRackReorderPreview = ({ rackRef, tiles }: UseRackReorderPreviewInput): RackReorderPreview => {
  const [preview, setPreview] = useState<{ from: number; to: number } | null>(null);
  /** Slot spans as they were at pickup, and which slot the tile in hand came from. */
  const spansRef = useRef<SlotSpan[]>([]);
  const fromRef = useRef<number | null>(null);
  /** The rack's own box at pickup — what "the pointer is over the rack" means for this gesture. */
  const boundsRef = useRef<Bounds | null>(null);
  /** Where the tile sits in the order on screen — what the swap tolerance is measured against. */
  const heldRef = useRef(0);

  // The drag listeners are registered once and never re-registered — dnd-kit delivers a gesture's
  // events to whoever was subscribed when it started — so everything they read that changes between
  // renders is read through a ref.
  const latest = useRef(tiles);
  useEffect(() => {
    latest.current = tiles;
  });

  const landingIndexAt = useCallback((x: number, y: number): number | null => {
    const from = fromRef.current;
    const bounds = boundsRef.current;
    if (from == null || bounds == null) return null;

    const pointer = toPage(x, y);
    if (!within(bounds, pointer.x, pointer.y)) return null;
    return landingIndex(spansRef.current, from, pointer.x, heldRef.current);
  }, []);

  const listeners = useMemo(
    () => ({
      onDragStart: (event: DragStartEvent) => {
        const current = latest.current;
        const from = current.findIndex((tile) => tile.id === event.operation.source?.id);
        fromRef.current = from === -1 ? null : from;
        heldRef.current = Math.max(from, 0);
        spansRef.current = from === -1 ? [] : measureSlots(rackRef.current, current.length);
        boundsRef.current = from === -1 ? null : measureRack(rackRef.current);
        setPreview(null);
      },
      onDragMove: (event: DragMoveEvent) => {
        const from = fromRef.current;
        if (from == null) return;

        // `to` is where this move is going; the operation's own position is still one move behind
        // while `dragmove` is being dispatched. See Board's handler for the same reading.
        const pointer = event.to ?? event.operation.position.current;
        const to = landingIndexAt(pointer.x, pointer.y);
        // Off the rack the tile would go back where it came from, and that is the place the tolerance
        // is measured from until it is over the rack again.
        heldRef.current = to ?? from;

        setPreview((held) => {
          if (to == null) return held == null ? held : null;
          return held?.from === from && held.to === to ? held : { from, to };
        });
      },
      // Only the preview is dropped here, never `fromRef` — the rack's own drag-end handler asks
      // `landingIndexAt` where the tile goes, and listener order between the two is nobody's to rely
      // on. The next pickup is what clears it.
      onDragEnd: () => setPreview(null),
    }),
    [rackRef, landingIndexAt],
  );
  useDragDropMonitor(listeners);

  const order = useMemo(
    () => (preview && preview.from < tiles.length ? moveTile(tiles, preview.from, preview.to) : tiles),
    [tiles, preview],
  );

  return { order, landingIndexAt };
};
